import { OrderClientMetadata, VisitorIdentitySnapshot } from '../types';
import { storageService } from './storage';

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof window.document !== 'undefined';

const generateVisitorId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    const randomSegment = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    return `${Date.now().toString(16)}-${randomSegment()}${randomSegment()}-${randomSegment()}${randomSegment()}`;
};

const ensureIdentity = (): VisitorIdentitySnapshot => {
    const nowIso = new Date().toISOString();
    const existing = storageService.getVisitorIdentity();

    if (!existing) {
        const created: VisitorIdentitySnapshot = {
            id: generateVisitorId(),
            createdAt: nowIso,
            lastSeenAt: nowIso,
            visitCount: 1
        };
        storageService.setVisitorIdentity(created);
        return created;
    }

    return existing;
};

export const initializeVisitorIdentity = (): VisitorIdentitySnapshot | null => {
    if (!isBrowser()) {
        return null;
    }

    const current = ensureIdentity();
    const nowIso = new Date().toISOString();
    const next: VisitorIdentitySnapshot = {
        ...current,
        lastSeenAt: nowIso,
        visitCount: Math.max(current.visitCount ?? 0, 0) + 1,
    };
    storageService.setVisitorIdentity(next);
    return next;
};

export const getVisitorIdentitySnapshot = (): VisitorIdentitySnapshot | null => {
    if (!isBrowser()) {
        return null;
    }

    return ensureIdentity();
};

export const collectClientMetadata = (): OrderClientMetadata | null => {
    if (!isBrowser()) {
        return null;
    }

    const identity = getVisitorIdentitySnapshot();
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    const doc = typeof document !== 'undefined' ? document : undefined;
    const win = typeof window !== 'undefined' ? window : undefined;

    const timezone = typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined;

    const screenMetadata = win?.screen
        ? {
            width: win.screen.width,
            height: win.screen.height,
            availWidth: win.screen.availWidth,
            availHeight: win.screen.availHeight,
            pixelRatio: typeof win.devicePixelRatio === 'number' ? win.devicePixelRatio : undefined
        }
        : undefined;

    const viewportMetadata = typeof win?.innerWidth === 'number' && typeof win?.innerHeight === 'number'
        ? {
            width: win.innerWidth,
            height: win.innerHeight
        }
        : undefined;

    const navAny = nav as (Navigator & {
        deviceMemory?: number;
        userAgentData?: {
            brands?: Array<{ brand: string; version: string }>;
            mobile?: boolean;
            platform?: string;
        };
    }) | undefined;

    const deviceMemory = typeof navAny?.deviceMemory === 'number' ? navAny.deviceMemory : undefined;
    const hardwareConcurrency = typeof nav?.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : undefined;

    const colorScheme = typeof win?.matchMedia === 'function'
        ? (win.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : undefined;

    const metadata: OrderClientMetadata = {
        visitorId: identity?.id ?? generateVisitorId(),
        identityCreatedAt: identity?.createdAt,
        identityLastSeenAt: identity?.lastSeenAt,
        identityVisitCount: identity?.visitCount,
        userAgent: nav?.userAgent,
        language: nav?.language,
        languages: Array.isArray(nav?.languages) ? [...nav.languages] : undefined,
        platform: nav?.platform,
        timezone,
        screen: screenMetadata,
        viewport: viewportMetadata,
        referrer: doc?.referrer ? doc.referrer : undefined,
        currentUrl: win?.location?.href,
        hardwareConcurrency,
        deviceMemory,
        colorScheme
    };

    if (navAny?.userAgentData) {
        metadata.extra = {
            brands: navAny.userAgentData.brands,
            mobile: navAny.userAgentData.mobile,
            platform: navAny.userAgentData.platform
        };
    }

    return metadata;
};
